/**
 * NP-490 — the authenticated borrower's profile, including their avatar.
 *
 * `useAuth().user` cannot carry the avatar: it is hydrated from localStorage at
 * login, and the API deliberately leaves AvatarUrl null on auth responses so token
 * exchanges never carry image bytes. So the header and Settings read the profile
 * from `GET /api/v1/borrower/me` through this shared query instead.
 *
 * Both surfaces using one query key is the point: uploading from Settings
 * invalidates it, so the header avatar updates in the same beat — which is the half
 * of the bug QA actually noticed ("not reflected in the profile icon at the
 * upper-right corner").
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/services/api-client';

export interface BorrowerProfileResponse {
  borrower?: {
    borrowerId?: string;
    email?: string;
    firstName?: string;
    lastName?: string;
    phone?: string | null;
    preferredLanguage?: string;
    /** Base64 data URL, or null/absent when the borrower has no avatar. */
    avatarUrl?: string | null;
  };
  hasAcceptedEConsent?: boolean;
}

export const borrowerProfileKeys = {
  me: ['borrower', 'me'] as const,
};

export function useBorrowerProfile() {
  const query = useQuery({
    queryKey: borrowerProfileKeys.me,
    queryFn: () =>
      apiClient.get<BorrowerProfileResponse>('/api/v1/borrower/me', {
        // The header renders on every page; a transient failure should leave the
        // initials in place, not raise a global toast the borrower can do nothing
        // about. Retries are left to React Query rather than stacking on the
        // apiClient's own — the same treatment useDocumentTypes uses.
        retries: 0,
        suppressToast: true,
      }),
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });

  return {
    profile: query.data?.borrower,
    avatarUrl: query.data?.borrower?.avatarUrl ?? null,
    isLoading: query.isPending,
    error: query.error,
  };
}

/** PUT /api/v1/borrower/me/avatar — `image` is a base64 data URL. */
export function useSetAvatar() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (image: string) =>
      apiClient.put<BorrowerProfileResponse>('/api/v1/borrower/me/avatar', { image }),
    onSuccess: () => {
      // Refetch rather than writing the response into the cache: the server is the
      // authority on what was actually stored, and this is the moment the header
      // must catch up.
      queryClient.invalidateQueries({ queryKey: borrowerProfileKeys.me });
    },
  });
}

/** DELETE /api/v1/borrower/me/avatar. Idempotent server-side. */
export function useDeleteAvatar() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => apiClient.delete<BorrowerProfileResponse>('/api/v1/borrower/me/avatar'),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: borrowerProfileKeys.me });
    },
  });
}
